import { LocalizationService } from '@abp/ng.core';
import { Confirmation, ConfirmationService, ToasterService } from '@abp/ng.theme.shared';
import { Component, EventEmitter, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { Store } from '@ngxs/store';
import { map, take } from 'rxjs';
import { FormGroupStatus } from 'src/core/constants/form/form-group-status.constant';
import { LLMProvider } from 'src/core/models/generic-lookup-type/llm/llm-provider.glt';
import { GenericLookupDto } from 'src/core/models/generic-lookup/generic-lookup.dto';
import { LLMConfigurationDto } from 'src/core/models/llm/llm-configuration.dto';
import { CrudService } from 'src/core/services/crud/crud.service';
import { LLMConfigurationService } from 'src/core/services/llm/llm-configuration.service';
import { GenericLookupTypeState } from 'src/core/states/generic-lookup-type/generic-lookup-type.state';
import { LLMConfigurationNameValidator } from 'src/core/validators/llm/llm-configuration-name.validator';
import { FormButtonConfiguration } from 'src/ca-shared/form/form-button-configuration.model';
import { LLMProviderModelListDto } from 'src/core/models/llm/llm-provider-model-list.dto';

@Component({
  selector: 'ca-llm-configuration-form',
  templateUrl: './llm-configuration-form.component.html',
  styleUrl: './llm-configuration-form.component.scss',
  standalone: false,
})
export class LLMConfigurationFormComponent {
  @Output()
  saved: EventEmitter<{
    id: number;
  }> = new EventEmitter();

  @Output()
  deleted: EventEmitter<{
    id: number;
  }> = new EventEmitter();

  @Output()
  closed = new EventEmitter();

  get isOpenAIProvider() {
    return this.llmConfigurationForm?.get('llmProviderId').value == LLMProvider.openAI;
  }

  get isAzureProvider() {
    return this.llmConfigurationForm?.get('llmProviderId').value == LLMProvider.azure;
  }

  get isProxyProvider() {
    return this.llmConfigurationForm?.get('llmProviderId').value == LLMProvider.proxy;
  }

  get isAwsBedrockProvider() {
    return this.llmConfigurationForm?.get('llmProviderId').value == LLMProvider.awsBedrock;
  }

  get isGeminiProvider() {
    return this.llmConfigurationForm?.get('llmProviderId').value == LLMProvider.gemini;
  }

  get isGroqProvider() {
    return this.llmConfigurationForm?.get('llmProviderId').value == LLMProvider.groq;
  }

  get isTogetherAiProvider() {
    return this.llmConfigurationForm?.get('llmProviderId').value == LLMProvider.togetherAi;
  }

  get isVertexAiProvider() {
    return this.llmConfigurationForm?.get('llmProviderId').value == LLMProvider.vertexAi;
  }

  get isPrivateLLMProvider() {
    return this.llmConfigurationForm?.get('llmProviderId').value == LLMProvider.privateLLM;
  }

  get supportsReasoningEffort() {
    return this.isProxyProvider || this.isPrivateLLMProvider;
  }

  get isDynamicModelProvider() {
    return (
      this.isOpenAIProvider ||
      this.isProxyProvider ||
      this.isAwsBedrockProvider ||
      this.isGeminiProvider ||
      this.isGroqProvider ||
      this.isTogetherAiProvider ||
      this.isVertexAiProvider
    );
  }

  get requiresApiKey() {
    const provider = this.llmConfigurationForm?.get('llmProviderId').value;
    return [
      LLMProvider.openAI,
      LLMProvider.azure,
      LLMProvider.proxy,
      LLMProvider.awsBedrock,
      LLMProvider.gemini,
      LLMProvider.groq,
      LLMProvider.togetherAi,
      LLMProvider.privateLLM,
      LLMProvider.vertexAi,
    ].some(p => p == provider);
  }

  get activeModelList(): string[] {
    const providerKey = this.currentProviderKey();
    if (providerKey) {
      return this.providerModels[providerKey] ?? [];
    }
    return [];
  }

  get fetchingActiveProviderModels(): boolean {
    const providerKey = this.currentProviderKey();
    return providerKey ? !!this.fetchingProviderModels[providerKey] : false;
  }

  get formButtonConfiguration(): FormButtonConfiguration {
    const result: FormButtonConfiguration = {
      delete: {
        name: this.localizationService.instant('AbpUi::Delete'),
        visible: this.llmConfiguration?.id > 0,
        disabled: false,
        tooltip: this.localizationService.instant('AbpUi::Delete'),
      },
    };

    return result;
  }

  items: [];
  llmConfigurationForm: FormGroup;
  llmConfiguration: LLMConfigurationDto;
  processing = false;
  columnsToDisplay: string[] = ['lookupValue', 'delete'];
  llmConfigurationNameValidator: LLMConfigurationNameValidator;
  validators: Validators;
  valid = true;
  invalidIndex: number;
  invalidErrorMessage: string;
  processingMessage = this.localizationService.instant('AbpUi::ProcessingWithThreeDot');
  llmProviders: GenericLookupDto[] = [];
  providerModels: { [key: string]: string[] } = {};
  fetchingProviderModels: { [key: string]: boolean } = {};

  private static readonly REASONING_EFFORT_DEFAULT = 'default';

  private static readonly PROVIDER_API_KEY_OPENAI = 'openai';
  private static readonly PROVIDER_API_KEY_PROXY = 'proxy';
  private static readonly PROVIDER_API_KEY_BEDROCK = 'bedrock';
  private static readonly PROVIDER_API_KEY_GEMINI = 'gemini';
  private static readonly PROVIDER_API_KEY_GROQ = 'groq';
  private static readonly PROVIDER_API_KEY_TOGETHER_AI = 'together_ai';
  private static readonly PROVIDER_API_KEY_VERTEX_AI = 'vertex_ai';

  constructor(
    private service: CrudService,
    private fb: FormBuilder,
    private store: Store,
    private confirmationService: ConfirmationService,
    private localizationService: LocalizationService,
    private llmConfigurationService: LLMConfigurationService,
    private toastr: ToasterService,
  ) {
    this.llmConfigurationNameValidator = new LLMConfigurationNameValidator(
      this.llmConfigurationService,
    );

    this.store
      .select(GenericLookupTypeState.getGenericLookups)
      .pipe(map(filterFn => filterFn(LLMProvider)))
      .subscribe(result => {
        this.llmProviders = result;
      });

    this.llmConfigurationForm = this.fb.group({
      id: [0],
      llmProviderId: [null, Validators.required],
      generativeAIModel: [
        null,
        {
          validators: [
            this.providerBasedValidatorAny([
              LLMProvider.openAI,
              LLMProvider.proxy,
              LLMProvider.awsBedrock,
              LLMProvider.gemini,
              LLMProvider.groq,
              LLMProvider.togetherAi,
              LLMProvider.privateLLM,
              LLMProvider.vertexAi,
            ]),
          ],
        },
      ],
      apiVersion: [
        null,
        {
          validators: [
            this.providerBasedValidatorAny([
              LLMProvider.azure,
              LLMProvider.awsBedrock,
              LLMProvider.vertexAi,
            ]),
            Validators.maxLength(200),
          ],
        },
      ],
      resourceName: [
        null,
        {
          validators: [
            this.providerBasedValidatorAny([
              LLMProvider.azure,
              LLMProvider.proxy,
              LLMProvider.awsBedrock,
              LLMProvider.privateLLM,
              LLMProvider.vertexAi,
            ]),
            Validators.maxLength(200),
          ],
        },
      ],
      deploymentName: [
        null,
        {
          validators: [
            this.providerBasedValidatorAny([LLMProvider.azure, LLMProvider.vertexAi]),
            Validators.maxLength(200),
          ],
        },
      ],
      tokenUri: [
        null,
        { validators: [this.providerBasedValidator(LLMProvider.vertexAi), Validators.maxLength(200)] },
      ],
      apiKey: [null, { validators: [this.apiKeyValidator(), this.apiKeyMaxLengthValidator()] }],
      reasoningEffort: [
        LLMConfigurationFormComponent.REASONING_EFFORT_DEFAULT,
        { validators: [Validators.maxLength(50)] },
      ],
      name: [
        null,
        {
          validators: [
            Validators.required,
            Validators.pattern(/^[a-zA-Z0-9_-]{1,200}$/),
            Validators.maxLength(200),
          ],
          asyncValidators: [this.llmConfigurationNameValidator],
        },
      ],
    });
  }

  ngOnInit() {}

  load(id: number | null) {
    this.items = [];
    this.resetForm();
    this.llmConfigurationNameValidator.id = id;
    this.providerModels = {};
    this.fetchingProviderModels = {};

    if (id == null) {
      this.llmConfiguration = {
        id: 0,
        name: null,
        lastModificationTime: null,
        llmProviderName: null,
        apiKey: null,
        createdBy: null,
        resourceName: null,
        apiVersion: null,
        deploymentName: null,
        tokenUri: null,
        generativeAIModel: null,
        reasoningEffort: null,
        llmProviderId: null,
      };

      this.llmConfigurationForm.patchValue({
        ...this.llmConfiguration,
        reasoningEffort: LLMConfigurationFormComponent.REASONING_EFFORT_DEFAULT,
      });
    } else {
      this.processingMessage = this.localizationService.instant('CA::Loading') + '...';
      this.processing = true;

      this.service
        .getById<LLMConfigurationDto>(LLMConfigurationDto, id)
        .pipe(take(1))
        .subscribe(data => {
          this.llmConfiguration = data;
          this.llmConfigurationForm.patchValue({
            ...this.llmConfiguration,
            reasoningEffort:
              this.llmConfiguration.reasoningEffort ??
              LLMConfigurationFormComponent.REASONING_EFFORT_DEFAULT,
          });
          this.llmConfigurationForm.get('llmProviderId').updateValueAndValidity();

          const providerKey = this.providerKeyFor(this.llmConfiguration.llmProviderId);
          if (providerKey && this.llmConfiguration.generativeAIModel) {
            this.providerModels[providerKey] = [this.llmConfiguration.generativeAIModel];
          }

          this.processing = false;
          this.processingMessage = this.localizationService.instant(
            'AbpUi::ProcessingWithThreeDot',
          );
        });
    }
  }

  closePanel() {
    this.llmConfigurationForm.reset();

    this.closed.emit();
  }

  onSubmitForm() {
    var providerID = this.llmConfigurationForm?.get('llmProviderId').value;

    if (
      this.processing ||
      this.llmConfigurationForm.invalid ||
      this.llmConfigurationForm.disabled
    ) {
      return;
    }

    this.processing = true;

    if (this.llmConfigurationForm.valid) {
      this.saveLLMConfiguration();
    } else if (this.llmConfigurationForm.pending) {
      const subscription = this.llmConfigurationForm.statusChanges.subscribe(status => {
        if (status === FormGroupStatus.VALID) {
          subscription.unsubscribe();
          this.saveLLMConfiguration();
        } else if (status === FormGroupStatus.PENDING) {
          this.processing = true;
        } else {
          subscription.unsubscribe();
          this.processing = false;
        }
      });
    }
  }

  saveLLMConfiguration() {
    const llmConfiguration: LLMConfigurationDto = this.llmConfigurationForm.value;

    llmConfiguration.id = llmConfiguration.id == null ? 0 : llmConfiguration.id;
    llmConfiguration.apiKey = llmConfiguration.apiKey?.trim();

    if (
      llmConfiguration.reasoningEffort === LLMConfigurationFormComponent.REASONING_EFFORT_DEFAULT
    ) {
      llmConfiguration.reasoningEffort = null;
    }

    this.service
      .save<LLMConfigurationDto>(LLMConfigurationDto, llmConfiguration)
      .pipe(take(1))
      .subscribe({
        next: data => {
          this.saved.emit({ id: data.id });
          this.llmConfigurationForm.reset();
          this.processing = false;
        },
        error: () => {
          this.processing = false;
          return null;
        },
      });
  }

  deleteLLMConfiguration(eventArgs) {
    this.processing = true;

    this.service
      .delete<LLMConfigurationDto>(LLMConfigurationDto, this.llmConfiguration.id)
      .pipe(take(1))
      .subscribe({
        next: data => {
          this.processing = false;
          this.deleted.emit({ id: this.llmConfiguration.id });
          this.llmConfigurationForm.reset();
        },
        error: () => {
          this.processing = false;
          return null;
        },
      });
  }

  resetForm() {
    this.llmConfigurationForm?.reset();
  }

  confirmDeletion(eventArgs, name) {
    this.confirmationService
      .warn(
        'Settings::LLMConfiguration:DeletionConfirmationMessage',
        'Settings::LLMConfiguration:DeletionConfirmationTitle',
        {
          messageLocalizationParams: [name],
          yesText: '::Delete',
        },
      )
      .pipe(take(1))
      .subscribe((status: Confirmation.Status) => {
        if (status === Confirmation.Status.confirm) {
          this.deleteLLMConfiguration(eventArgs);
        }
      });
  }

  providerBasedValidator(providerId) {
    return (control: AbstractControl) => {
      var selectedProviderID = this.llmConfigurationForm?.get('llmProviderId').value;
      if (selectedProviderID === providerId && !control.value) {
        return { required: true };
      }
    };
  }

  providerBasedValidatorAny(providerIds: number[]) {
    return (control: AbstractControl) => {
      var selectedProviderID = this.llmConfigurationForm?.get('llmProviderId').value;
      if (providerIds.some(p => p == selectedProviderID) && !control.value) {
        return { required: true };
      }
    };
  }

  apiKeyValidator() {
    return (control: AbstractControl) => {
      var id = this.llmConfigurationForm?.get('id').value;
      var selectedProviderID = this.llmConfigurationForm?.get('llmProviderId').value;

      if (
        [
          LLMProvider.openAI,
          LLMProvider.azure,
          LLMProvider.proxy,
          LLMProvider.awsBedrock,
          LLMProvider.gemini,
          LLMProvider.groq,
          LLMProvider.togetherAi,
          LLMProvider.privateLLM,
          LLMProvider.vertexAi,
        ].filter(x => x == selectedProviderID).length > 0 &&
        control.value === null &&
        id == 0
      ) {
        return { required: true };
      }
    };
  }

  apiKeyMaxLengthValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      var selectedProviderID = this.llmConfigurationForm?.get('llmProviderId').value;
      return selectedProviderID === LLMProvider.vertexAi
        ? Validators.maxLength(4000)(control)
        : Validators.maxLength(200)(control);
    };
  }

  onProviderChange(event: any) {
    this.llmConfigurationForm.get('generativeAIModel').setValue(null);
    this.providerModels = {};

    const providerKey = this.providerKeyFor(event);
    if (providerKey) {
      this.fetchProviderModels(providerKey, false);
    }

    this.llmConfigurationForm.get('generativeAIModel').updateValueAndValidity();
    this.llmConfigurationForm.get('apiVersion').updateValueAndValidity();
    this.llmConfigurationForm.get('resourceName').updateValueAndValidity();
    this.llmConfigurationForm.get('deploymentName').updateValueAndValidity();
    this.llmConfigurationForm.get('tokenUri').updateValueAndValidity();
    this.llmConfigurationForm.get('apiKey').updateValueAndValidity();
  }

  onFetchActiveProviderModels() {
    const providerKey = this.currentProviderKey();
    if (providerKey) {
      this.fetchProviderModels(providerKey, true);
    }
  }

  private providerKeyFor(providerId: number): string | null {
    if (providerId == LLMProvider.openAI) {
      return LLMConfigurationFormComponent.PROVIDER_API_KEY_OPENAI;
    }
    if (providerId == LLMProvider.proxy) {
      return LLMConfigurationFormComponent.PROVIDER_API_KEY_PROXY;
    }
    if (providerId == LLMProvider.awsBedrock) {
      return LLMConfigurationFormComponent.PROVIDER_API_KEY_BEDROCK;
    }
    if (providerId == LLMProvider.gemini) {
      return LLMConfigurationFormComponent.PROVIDER_API_KEY_GEMINI;
    }
    if (providerId == LLMProvider.groq) {
      return LLMConfigurationFormComponent.PROVIDER_API_KEY_GROQ;
    }
    if (providerId == LLMProvider.togetherAi) {
      return LLMConfigurationFormComponent.PROVIDER_API_KEY_TOGETHER_AI;
    }
    if (providerId == LLMProvider.vertexAi) {
      return LLMConfigurationFormComponent.PROVIDER_API_KEY_VERTEX_AI;
    }
    return null;
  }

  private currentProviderKey(): string | null {
    return this.providerKeyFor(this.llmConfigurationForm?.get('llmProviderId').value);
  }

  private fetchProviderModels(providerKey: string, announceEmpty: boolean) {
    const configurationId = this.llmConfigurationForm.get('id')?.value;
    const isEditing = configurationId > 0;

    if (providerKey === LLMConfigurationFormComponent.PROVIDER_API_KEY_PROXY) {
      const apiKey = this.llmConfigurationForm.get('apiKey')?.value;
      const baseUrl = this.llmConfigurationForm.get('resourceName')?.value;
      if (!baseUrl || (!isEditing && !apiKey)) {
        return;
      }
    }

    this.fetchingProviderModels = { ...this.fetchingProviderModels, [providerKey]: true };

    const request$ = providerKey === LLMConfigurationFormComponent.PROVIDER_API_KEY_PROXY
      ? this.llmConfigurationService.getProxyModelList(
          this.llmConfigurationForm.get('apiKey').value,
          this.llmConfigurationForm.get('resourceName').value,
          isEditing ? configurationId : undefined
        )
      : this.service.abpGetById<LLMProviderModelListDto>(LLMProviderModelListDto, providerKey);

    request$.pipe(take(1)).subscribe({
        next: data => {
          this.fetchingProviderModels = { ...this.fetchingProviderModels, [providerKey]: false };
          const fetched = data?.models ?? [];

          if (fetched.length === 0) {
            if (announceEmpty) {
              this.toastr.info(
                this.localizationService.instant('Settings::LLMConfiguration:NoModelsAvailable'),
              );
            }
            return;
          }

          this.providerModels = { ...this.providerModels, [providerKey]: fetched };

          const currentModel = this.llmConfigurationForm.get('generativeAIModel')?.value;
          if (!currentModel || !fetched.includes(currentModel)) {
            this.llmConfigurationForm.get('generativeAIModel').setValue(fetched[0]);
          }
          this.llmConfigurationForm.get('generativeAIModel').updateValueAndValidity();
        },
        error: () => {
          this.fetchingProviderModels = { ...this.fetchingProviderModels, [providerKey]: false };
          this.toastr.error(
            this.localizationService.instant('Settings::LLMConfiguration:FetchModelsFailed'),
          );
        },
      });
  }
}
